<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';

export default{
  name: 'UploadFileModal',
  data(){
    return{
      isLoading: false,
      condoId: this.$route.params.condoId,
      buttonType: [
        {name: 'Notice', value: 'notice', icon: 'document'},
        {name: 'MOM', value: 'meeting_minutes', icon: 'document'},
        {name: 'House Rules', value: 'house_rules', icon: 'document'},
      ],
      selectedType: 'notice',
      attachedFiles: [],
      attachFileErrMsg: null,
      documentTitle: '',
      errMsg: {
        document_title: '',
        attached_files: '',
      }
    }
  },
  computed: {
    acceptedFileTypes() {
      return '.pdf';
    },
    isFormValid(){
      return this.documentTitle.length > 0 && this.attachedFiles.length > 0
    }
  },
  methods: {
    ...mapActions('condoMngt', ['uploadDoc']),
    handleAttachFile(){
      this.$refs.fileUpload.click();
    },
    handleDragOver(event){
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    handleDrop(event){
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },
    handleFileSelect(event){
      const files = Array.from(event.target.files);
      this.processFiles(files);
      event.target.value = ""; 
    },
    processFiles(files){
      // validate file
      const allowedTypes = ['application/pdf'];
      const maxSize = 15 * 1024 * 1024; // 15MB

      // Only process the first file if multiple files are dropped
      const file = files[0];
      
      if (!file) {
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        this.attachFileErrMsg = "Only PDF files are supported.";
        return;
      }

      if (file.size > maxSize) {
        this.attachFileErrMsg = "File size must be under 15 MB.";
        return;
      }

      // Save file to local state
      this.attachFileErrMsg = null;
      this.attachedFiles = [file]; // Replace existing files with new file
    },
    handleRemoveFile(index){
      this.attachedFiles.splice(index, 1)
    },
    async handleUpload(){
      this.isLoading = true;
      this.errMsg = {
        document_title: '',
        attached_files: '',
      }
      if(!this.isFormValid){
        useAlert('Please fill in the required fields')
        if(this.documentTitle.length <= 0){
          this.errMsg.document_title = 'This field is required'
        }
        if(this.attachedFiles.length <= 0){
          this.errMsg.attached_files = 'Please upload at least 1 document'
        }
        this.isLoading = false;
        return
      }
      const formData = new FormData();
      formData.append('file', this.attachedFiles[0]);
      formData.append('title', this.documentTitle);
      formData.append('document_type', this.selectedType)

      try{
        await this.uploadDoc({
          condoId: this.condoId, formData
        })
        useAlert('Document uploaded!')
        this.$emit('refresh');
        this.$emit('close');
      }catch(err){
        if (Array.isArray(err)) {
          useAlert(err.join('\n'))
        } else {
          useAlert(`Failed to fetch condo`)
          console.error(err);
        }
      }finally{
        this.isLoading = false;
      }
    }
  }
}
</script>

<template>
  <div class="fixed inset-0 flex items-center justify-center bg-slate-800/50 z-50">
    <div class="bg-white dark:bg-slate-800 max-h-[80vh] overflow-y-auto rounded-lg p-6 shadow-lg min-w-3xl border border-slate-200 dark:border-slate-700">
      <!-- header -->
      <div class="flex items-center mb-4 justify-between">
        <div class="flex gap-2 items-center">
          <fluent-icon icon="upload" class="text-slate-700 dark:text-slate-200" />
          <h1 class="text-xl font-bold mb-0 text-slate-800 dark:text-slate-100">
            Upload Files
          </h1>
        </div>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <!-- select upload type -->
      <div class="grid gap-4 mb-8 grid-cols-2 md:grid-cols-4">
        <button
          type="button"
          v-for="button in buttonType"
          :key="button.value"
          @click="selectedType = button.value"
          :class="{
            'px-4 py-2 rounded border transition': true,
            'bg-woot-500 text-white border-woot-700': selectedType === button.value,
            'bg-slate-200 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200': selectedType !== button.value,
          }"
        >
          {{ button.name }}
        </button>
      </div>

      <!-- document title -->
      <div class="mb-4">
        <label for="document_title">Document Title:</label>
        <input 
          v-model="documentTitle" 
          id="document_title" 
          type="text" 
          placeholder="Enter a title..."
          :class="{
            'reset-base p-2 rounded w-full mb-0 border text-slate-800': true,
            'border-slate-200': !errMsg.document_title,
            'border-red-500': errMsg.document_title
          }"
        />
        <p v-if="errMsg.document_title" class="text-red-500">{{ errMsg.document_title }}</p>
      </div>
      
      <!-- attach file section -->
      <div
        class="w-full p-8 h-50 mb-4 cursor-pointer rounded border text-center  bg-slate-50 hover:bg-slate-100 border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:border-slate-600"
        @click="handleAttachFile"
        @dragover.prevent.stop="handleDragOver"
        @drop.prevent.stop="handleDrop"
      >
        <input
          ref="fileUpload"
          type="file"
          @change="handleFileSelect"
          class="hidden"
          :accept="acceptedFileTypes"
        />
        <p class="mb-4 text-slate-700 dark:text-slate-200">
          Drag files here or
          <span class="underline text-woot-500">click to select files</span>
        </p>
        <p class="text-xs text-slate-600 dark:text-slate-400">
          Only PDF files are supported. Maximum file size: 15MB.
        </p>
        <p v-if="attachFileErrMsg" class="text-red-500 mt-2">
          {{ attachFileErrMsg }}
        </p>
        <p v-if="errMsg.attached_files" class="text-red-500 mt-2">{{ errMsg.attached_files }}</p>
      </div>

      <!-- render uploaded files section -->
      <div class="my-4 space-y-2">
        <div
          v-for="(file, index) in attachedFiles"
          :key="file.name + file.size"
          class="flex items-center justify-between p-3 rounded-lg border shadow-sm transition bg-slate-50 hover:bg-slate-100 border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:border-slate-600"
        >
          <div class="flex items-center gap-3">
            <fluent-icon icon="document" class="h-6 w-6 text-woot-500" />
            <div class="flex flex-col">
              <span class="font-medium text-slate-800 dark:text-slate-100 truncate max-w-xs">
                {{ file.name }}
              </span>
              <span class="text-xs text-slate-500 dark:text-slate-400">
                {{ (file.size / 1000000).toFixed(2) }} MB
              </span>
            </div>
          </div>

          <woot-button
            icon="dismiss"
            color-scheme="alert"
            variant="clear"
            @click="handleRemoveFile(index)"
          />
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-end gap-4 mt-4">
        <woot-button color-scheme="secondary" :is-loading="isLoading" @click="$emit('close')">Cancel</woot-button>
        <woot-button color-scheme="success" :is-loading="isLoading" @click="handleUpload">Upload</woot-button>
      </div>
    </div>
  </div>
</template>
